import { useState } from "react";
import { IProgressEvent } from "@api";

import { useDataMutation } from "@Libs/DataLayer/hooks/useDataMutation";

import {
  IPresignedUrlPayload,
  getPresignedUrlService,
  uploadPhotoBookService,
  attachUploadedPhotoToServiceProviderService,
} from "@modules/ServiceProviders/services/photoBook";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { IPhoto } from "@modules/ServiceProviders/Containers/PhotoBookRouter/Router";
import { IPhotoUploadBoxProps } from "@modules/ServiceProviders/Components/PhotoBook/PhotoUploadBox";
import i18n from "@I18n/services/i18n";
import messages from "@modules/ServiceProviders/Components/messages";

export const PORTFOLIO_QUERY_KEY = "portfolio";

const isFileValid = (
  file: File,
  showToast: IPhotoUploadBoxProps["showToast"]
) => {
  const maxSize = 10 * 1024 * 1024; // 10 MB
  const allowedTypes = ["image/png", "image/jpg", "image/jpeg", "image/webp"];
  let errorMessage;

  if (file.size > maxSize) {
    errorMessage = i18n.formatMessage(
      messages.photoBookSizeValidationErrorMessage
    );
  } else if (!allowedTypes.includes(file.type)) {
    errorMessage = i18n.formatMessage(
      messages.photoBookFileTypeValidationErrorMessage
    );
  }

  if (errorMessage) {
    showToast({
      status: "error",
      description: errorMessage,
    });
    return false;
  }
  return true;
};

export const usePhotoBookUpload = (
  preUploadPayload: IPresignedUrlPayload,
  showToast: IPhotoUploadBoxProps["showToast"]
) => {
  const { currentUser } = useCurrentUser({ includes: ["serviceProvider"] });

  const [uploadProgress, setProgress] = useState<string | number>();

  const {
    data: presignedUrlResponse,
    isLoading: isLoadingPresignedUrl,
    isError: isErrorPresignedUrl,
    mutate: getUploadUrl,
  } = useDataMutation(() => getPresignedUrlService(preUploadPayload), {
    onSuccess: (data) =>
      uploadPhoto({ url: data.presignedUrl, file: preUploadPayload.rawFile }),
  });

  const {
    mutate: uploadPhoto,
    isLoading: isLoadingUploadPhoto,
    isError: isErrorUploadingPhoto,
  } = useDataMutation(
    (uploadParams: { url: string; file: File }) =>
      uploadPhotoBookService(uploadParams.url, uploadParams.file, {
        onUploadProgress: ({ progress = 0 }: IProgressEvent) => {
          setProgress((progress * 100).toFixed(0));
        },
      }),
    { onSuccess: () => attachUploadedPhotoToServiceProvider() }
  );

  const {
    data: uploadedData,
    mutate: attachUploadedPhotoToServiceProvider,
    isLoading: isLoadingAttachPhoto,
    isError: isErrorAttachingPhoto,
    isSuccess,
  } = useDataMutation(() =>
    attachUploadedPhotoToServiceProviderService({
      filename: presignedUrlResponse?.fileName,
      listworker_id: currentUser?.serviceProvider?.id,
    })
  );

  const uploadFile = () => {
    isFileValid(preUploadPayload?.rawFile, showToast) && getUploadUrl();
  };

  return {
    uploadFile,
    uploadedData: uploadedData as IPhoto,
    uploadProgress,

    isLoading:
      isLoadingPresignedUrl || isLoadingUploadPhoto || isLoadingAttachPhoto,

    isUploadSuccess: isSuccess,

    isUploadError:
      isErrorPresignedUrl || isErrorUploadingPhoto || isErrorAttachingPhoto,
  };
};
